import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import ParentWellnessNotifications from './ParentWellnessNotifications.jsx';
import './ParentDashboard.css';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const evDate = (e) => e.start_datetime || e.start_time || e.event_date;

// Action-first parent home: what needs doing + what's coming up, then each child.
function ParentDashboard() {
  const toast = useToast();
  let _user = null; try { _user = JSON.parse(localStorage.getItem('user')); } catch { /* ignore */ }
  const parentId = _user?.id;
  const firstName = (_user?.name || 'there').split(' ')[0];

  const [home, setHome] = useState({ children: [], totals: { waivers_missing: 0, outstanding: 0 } });
  const [announcements, setAnnouncements] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // connect-a-child (demoted; shown on demand or when no kids yet)
  const [qrToken, setQrToken] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [linkRequests, setLinkRequests] = useState([]);
  const [showConnect, setShowConnect] = useState(false);

  // expandable per-child detail
  const [openChild, setOpenChild] = useState(null);
  const [detail, setDetail] = useState({ videos: [], schedule: [] });

  const load = useCallback(async () => {
    setLoading(true);
    const s = new Date(); s.setHours(0, 0, 0, 0);
    const e = new Date(); e.setMonth(e.getMonth() + 2);
    const [h, a, u, lr, th] = await Promise.allSettled([
      api.get(`${API_BASE_URL}/api/parent/${parentId}/home`),
      api.get(`${API_BASE_URL}/api/announcements`),
      api.get(`${API_BASE_URL}/api/calendar/events?start_date=${s.toISOString()}&end_date=${e.toISOString()}`),
      api.get(`${API_BASE_URL}/api/parent/link-requests`),
      api.get(`${API_BASE_URL}/api/messages/threads`),
    ]);
    if (h.status === 'fulfilled') setHome(h.value.data || { children: [], totals: {} });
    if (a.status === 'fulfilled') setAnnouncements(Array.isArray(a.value.data) ? a.value.data : []);
    if (u.status === 'fulfilled') setUpcoming(Array.isArray(u.value.data) ? u.value.data : []);
    if (lr.status === 'fulfilled') setLinkRequests(Array.isArray(lr.value.data) ? lr.value.data : []);
    if (th.status === 'fulfilled') {
      const t = Array.isArray(th.value.data) ? th.value.data : [];
      setUnreadCount(t.reduce((n, x) => n + (x.unread_count > 0 ? 1 : 0), 0));
    }
    setLoading(false);
  }, [parentId]);
  useEffect(() => { load(); }, [load]);

  const generateQr = async () => {
    setQrLoading(true);
    try { const { data } = await api.post(`${API_BASE_URL}/api/parent/link-token`, {}); setQrToken(data); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed to generate link code'); }
    finally { setQrLoading(false); }
  };
  const qrUrl = qrToken ? `${window.location.origin}/link-parent/${qrToken.token}` : '';

  const toggleChild = async (athleteId) => {
    if (openChild === athleteId) { setOpenChild(null); return; }
    setOpenChild(athleteId);
    setDetail({ videos: [], schedule: [] });
    const [v, s] = await Promise.allSettled([
      api.get(`${API_BASE_URL}/api/parent/athlete/${athleteId}/videos`),
      api.get(`${API_BASE_URL}/api/parent/athlete/${athleteId}/schedule`),
    ]);
    setDetail({
      videos: v.status === 'fulfilled' && Array.isArray(v.value.data) ? v.value.data : [],
      schedule: s.status === 'fulfilled' && Array.isArray(s.value.data) ? s.value.data : [],
    });
  };

  const fmtDay = (dt) => (dt ? new Date(dt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) : '');
  const fmtTime = (dt) => (dt ? new Date(dt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '');
  const todayStart = new Date(new Date().setHours(0, 0, 0, 0));
  const upcomingSorted = [...upcoming]
    .filter(e => evDate(e) && new Date(evDate(e)) >= todayStart)
    .sort((a, b) => new Date(evDate(a)) - new Date(evDate(b)))
    .slice(0, 5);

  if (loading) return <div className="pd-loading">Loading your family…</div>;

  const { children, totals } = home;
  const hasKids = children.length > 0;
  const allClear = totals.waivers_missing === 0 && totals.outstanding === 0;

  return (
    <div className="pd">
      <div className="pd-head">
        <h1>👋 Hi {firstName}</h1>
        <Link to="/communication" className="pd-msg-btn">
          💬 Messages{unreadCount > 0 && <span className="pd-badge">{unreadCount}</span>}
        </Link>
      </div>

      {/* Needs your attention */}
      {hasKids && (
        <div className="pd-attention">
          {totals.waivers_missing > 0 && (
            <Link to="/sign-waivers" className="pd-action bad">
              <span className="pd-action-ico">📝</span>
              <span className="pd-action-main">
                <strong>{totals.waivers_missing} waiver{totals.waivers_missing > 1 ? 's' : ''} to sign</strong>
                <small>Tap to review &amp; sign</small>
              </span>
              <span className="pd-action-arrow">→</span>
            </Link>
          )}
          {totals.outstanding > 0 && (
            <Link to="/payment-portal" className="pd-action warn">
              <span className="pd-action-ico">💳</span>
              <span className="pd-action-main">
                <strong>{money(totals.outstanding)} due</strong>
                <small>View &amp; pay tuition</small>
              </span>
              <span className="pd-action-arrow">→</span>
            </Link>
          )}
          {allClear && (
            <div className="pd-action good">
              <span className="pd-action-ico">✅</span>
              <span className="pd-action-main"><strong>You're all caught up</strong><small>No waivers or payments pending</small></span>
            </div>
          )}
        </div>
      )}

      <ParentWellnessNotifications />

      <div className="pd-grid">
        {/* Upcoming */}
        <div className="pd-card">
          <div className="pd-card-head"><h2>📅 Upcoming</h2><Link to="/calendar" className="pd-link">Calendar →</Link></div>
          {upcomingSorted.length === 0 ? <p className="pd-empty">Nothing scheduled.</p> : (
            <ul className="pd-events">
              {upcomingSorted.map(e => (
                <li key={e.id}>
                  <span className="pd-ev-date">{fmtDay(evDate(e))}</span>
                  <span className="pd-ev-title">{e.title}<small>{fmtTime(evDate(e))}</small></span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Announcements */}
        <div className="pd-card">
          <div className="pd-card-head"><h2>📣 Announcements</h2><Link to="/communication" className="pd-link">All →</Link></div>
          {announcements.length === 0 ? <p className="pd-empty">No announcements.</p> : (
            <ul className="pd-anns">
              {announcements.slice(0, 3).map(a => (
                <li key={a.id}>
                  <span className="pd-ann-title">{a.title}</span>
                  <span className="pd-ann-body">{String(a.body || a.message || a.content || '').slice(0, 140)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Your children */}
      <div className="pd-kids">
        <h2>👧 Your {children.length === 1 ? 'child' : 'children'}</h2>
        {!hasKids ? (
          <div className="pd-nokids">
            <p>No children linked to your account yet.</p>
            <button className="pd-connect-btn" onClick={() => setShowConnect(true)}>＋ Connect your child</button>
          </div>
        ) : (
          <div className="pd-kid-cards">
            {children.map(c => (
              <div key={c.athlete_id} className={`pd-kid ${openChild === c.athlete_id ? 'open' : ''}`}>
                <button className="pd-kid-top" onClick={() => toggleChild(c.athlete_id)}>
                  <span className="pd-kid-name">{c.name}</span>
                  <span className="pd-kid-chips">
                    {c.waivers_required === 0 ? null : c.waivers_missing > 0
                      ? <span className="pd-chip bad">📝 {c.waivers_missing} to sign</span>
                      : <span className="pd-chip good">✓ waivers</span>}
                    {c.outstanding > 0
                      ? <span className="pd-chip warn">{money(c.outstanding)} due</span>
                      : <span className="pd-chip good">✓ paid</span>}
                    {c.classes.slice(0, 2).map((cl, i) => <span key={i} className="pd-chip">{cl}</span>)}
                  </span>
                  <span className="pd-kid-toggle">{openChild === c.athlete_id ? '▲' : '▼'}</span>
                </button>
                {openChild === c.athlete_id && (
                  <div className="pd-kid-detail">
                    <div className="pd-kid-sub">
                      <h4>Recent coach feedback</h4>
                      {detail.videos.length === 0 ? <p className="pd-empty">No videos yet.</p> : (
                        <ul className="pd-vids">
                          {detail.videos.slice(0, 4).map(v => (
                            <li key={v.id}><span>{v.file_name || 'Video'}</span><small>{v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}</small></li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="pd-kid-sub">
                      <h4>Classes</h4>
                      {c.classes.length === 0 ? <p className="pd-empty">Not enrolled.</p> : (
                        <ul className="pd-classes">{c.classes.map((cl, i) => <li key={i}>{cl}</li>)}</ul>
                      )}
                    </div>
                    <div className="pd-kid-actions">
                      <Link to={`/athlete-card/${c.athlete_id}?tab=performance`} className="pd-mini">📊 Progress</Link>
                      <Link to={`/athlete-card/${c.athlete_id}?tab=wellness`} className="pd-mini">🧘 Wellness</Link>
                      {c.waivers_missing > 0 && <Link to="/sign-waivers" className="pd-mini bad">Sign waivers</Link>}
                      {c.outstanding > 0 && <Link to="/payment-portal" className="pd-mini warn">Pay {money(c.outstanding)}</Link>}
                      <Link to="/calendar" className="pd-mini">Schedule</Link>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {hasKids && (
          <button className="pd-addchild" onClick={() => setShowConnect(v => !v)}>{showConnect ? 'Hide' : '＋ Add another child'}</button>
        )}
      </div>

      {/* Connect a child — demoted, shown on demand or when no kids linked */}
      {(showConnect || !hasKids) && (
        <div className="pd-card pd-connect">
          <h2>🔗 Connect your child</h2>
          <p className="pd-connect-sub">Have your gymnast open this link (or scan the code) while logged into their athlete account to link them to you.</p>
          <div className="pd-qr">
            {!qrToken ? (
              <button className="pd-connect-btn" onClick={generateQr} disabled={qrLoading}>{qrLoading ? 'Generating…' : 'Generate link code'}</button>
            ) : (
              <>
                <QRCodeSVG value={qrUrl} size={160} />
                <a href={qrUrl} target="_blank" rel="noreferrer" className="pd-qr-link">{qrUrl}</a>
              </>
            )}
          </div>
          {linkRequests.length > 0 && (
            <div className="pd-linkreqs">
              <h4>Match requests</h4>
              {linkRequests.map(r => (
                <div key={r.id} className="pd-linkreq">
                  <span>{r.child_name}{r.athlete_name ? ` → ${r.athlete_name}` : ''}</span>
                  <span className={`pd-chip ${r.status === 'approved' ? 'good' : r.status === 'rejected' ? 'bad' : 'warn'}`}>
                    {r.status === 'approved' ? '✓ Linked' : r.status === 'matched' ? '⏳ Awaiting admin' : r.status === 'rejected' ? '✗ Declined' : '⏳ Pending'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ParentDashboard;
